import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MessageSquare, Pin, Eye, MessageCircle, Plus, ChevronRight } from 'lucide-react';
import { forumAPI } from '../services/api';

const Forums = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  useEffect(() => { fetchData(); }, [selectedCategory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [catRes, postsRes] = await Promise.all([
        forumAPI.getCategories(),
        forumAPI.getPosts({ category: selectedCategory, limit: 20 }),
      ]);
      setCategories(catRes.data || []);
      setPosts(postsRes.data?.posts || postsRes.data || []);
    } catch {}
    setLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    setCreateLoading(true);
    try {
      await forumAPI.createPost({ title: newTitle, content: newContent, category_id: newCategory || categories[0]?.id });
      setShowCreate(false);
      setNewTitle(''); setNewContent(''); setNewCategory('');
      fetchData();
    } catch {}
    setCreateLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0E17]">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#F59E0B]/20 rounded-2xl flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-[#F59E0B]" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-medium text-[#F8FAFC]" data-testid="forums-title">Community Forums</h1>
              <p className="text-[#94A3B8]">Discuss movies, stars, and trading strategies</p>
            </div>
          </div>
          {user && (
            <Button onClick={() => setShowCreate(!showCreate)} className="bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold" data-testid="new-post-btn">
              <Plus className="w-4 h-4 mr-2" /> New Post
            </Button>
          )}
        </div>

        {showCreate && (
          <div className="bg-[#121826] rounded-2xl border border-[#1E293B] p-6 mb-8" data-testid="create-post-form">
            <h3 className="text-[#F8FAFC] font-bold text-lg mb-4 font-heading">Create New Post</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="text-[#94A3B8] text-sm font-medium mb-2 block">Category</label>
                <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)}
                  className="w-full bg-[#0A0E17] border border-[#1E293B] text-[#F8FAFC] rounded-md px-3 py-2 text-sm" data-testid="post-category-select">
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[#94A3B8] text-sm font-medium mb-2 block">Title</label>
                <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} required
                  className="bg-[#0A0E17] border-[#1E293B] text-[#F8FAFC]" placeholder="Post title" data-testid="post-title-input" />
              </div>
              <div>
                <label className="text-[#94A3B8] text-sm font-medium mb-2 block">Content</label>
                <textarea value={newContent} onChange={(e) => setNewContent(e.target.value)} required rows={4}
                  className="w-full bg-[#0A0E17] border border-[#1E293B] text-[#F8FAFC] rounded-md px-3 py-2 text-sm resize-none" placeholder="Write your thoughts..." data-testid="post-content-input" />
              </div>
              <div className="flex gap-3">
                <Button type="submit" disabled={createLoading} className="bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold" data-testid="submit-post-btn">
                  {createLoading ? 'Posting...' : 'Post'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="border-[#1E293B] text-[#94A3B8]">Cancel</Button>
              </div>
            </form>
          </div>
        )}

        <div className="grid lg:grid-cols-4 gap-8">
          <div>
            <div className="bg-[#121826] rounded-2xl border border-[#1E293B] overflow-hidden" data-testid="category-list">
              <div className="bg-[#1E293B] px-4 py-3">
                <h3 className="text-[#F8FAFC] font-bold text-sm font-heading">Categories</h3>
              </div>
              <div className="divide-y divide-[#1E293B]">
                <button onClick={() => setSelectedCategory('')}
                  className={`w-full text-left px-4 py-3 text-sm transition-colors ${!selectedCategory ? 'text-[#F59E0B] bg-[#F59E0B]/10' : 'text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1E293B]/50'}`}
                  data-testid="category-all">All Posts</button>
                {categories.map(c => (
                  <button key={c.id} onClick={() => setSelectedCategory(c.id)}
                    className={`w-full text-left px-4 py-3 text-sm transition-colors ${selectedCategory === c.id ? 'text-[#F59E0B] bg-[#F59E0B]/10' : 'text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1E293B]/50'}`}
                    data-testid={`category-${c.slug}`}>
                    {c.name}
                    <span className="text-[#64748B] text-xs ml-2">{c.post_count || 0}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            {loading ? (
              <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-[#121826] rounded-2xl border border-[#1E293B] animate-pulse" />)}</div>
            ) : posts.length > 0 ? (
              <div className="space-y-3">
                {posts.map((post) => (
                  <Link key={post.id} to={`/forum/${post.id}`}
                    className="block bg-[#121826] rounded-2xl border border-[#1E293B] p-5 hover:border-[#F59E0B]/50 hover:shadow-[0_0_20px_rgba(245,158,11,0.1)] transition-all duration-300 group"
                    data-testid={`forum-post-${post.id}`}>
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {post.is_pinned && <Pin className="w-3 h-3 text-[#F59E0B]" />}
                          <span className="text-[#64748B] text-xs bg-[#1E293B] px-2 py-0.5 rounded">{post.category_name || 'General'}</span>
                        </div>
                        <h3 className="text-[#F8FAFC] font-semibold group-hover:text-[#F59E0B] transition-colors mb-2">{post.title}</h3>
                        <p className="text-[#64748B] text-sm line-clamp-2 mb-3">{post.content}</p>
                        <div className="flex items-center gap-4 text-[#64748B] text-xs">
                          <span>by <span className="text-[#F59E0B]">{post.username}</span></span>
                          <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{post.views || 0}</span>
                          <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{post.reply_count || 0}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-[#64748B] flex-shrink-0 mt-1" />
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="bg-[#121826] rounded-2xl border border-[#1E293B] p-12 text-center">
                <MessageSquare className="w-10 h-10 text-[#64748B] mx-auto mb-3" />
                <p className="text-[#94A3B8]">No posts yet. Start the conversation!</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Forums;
